import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import MobileNav from '../../components/ui/MobileNav';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const SocialLinksSetup = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [existingLinks, setExistingLinks] = useState(null);
  const [formData, setFormData] = useState({
    instagramUrl: '',
    instagramFollowers: '',
    youtubeUrl: '',
    youtubeFollowers: '',
    tiktokUrl: '',
    tiktokFollowers: '',
    twitterUrl: '',
    twitterFollowers: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const platforms = [
    {
      id: 'instagram',
      name: 'Instagram',
      icon: 'Instagram',
      color: 'from-purple-500 to-pink-500',
      urlPlaceholder: 'https://instagram.com/username',
      urlField: 'instagramUrl',
      followersField: 'instagramFollowers',
      verifiedField: 'instagram_verified'
    },
    {
      id: 'youtube',
      name: 'YouTube',
      icon: 'Youtube',
      color: 'from-red-500 to-red-600',
      urlPlaceholder: 'https://youtube.com/@username',
      urlField: 'youtubeUrl',
      followersField: 'youtubeFollowers',
      verifiedField: 'youtube_verified'
    },
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: 'Music',
      color: 'from-cyan-400 to-pink-500',
      urlPlaceholder: 'https://tiktok.com/@username',
      urlField: 'tiktokUrl',
      followersField: 'tiktokFollowers',
      verifiedField: 'tiktok_verified'
    },
    {
      id: 'twitter',
      name: 'Twitter / X',
      icon: 'Twitter',
      color: 'from-blue-400 to-blue-600',
      urlPlaceholder: 'https://twitter.com/username',
      urlField: 'twitterUrl',
      followersField: 'twitterFollowers',
      verifiedField: 'twitter_verified'
    }
  ];

  useEffect(() => {
    if (user) {
      fetchExistingSocialLinks();
    }
  }, [user]);

  const fetchExistingSocialLinks = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        ?.from('social_links')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.maybeSingle();

      if (fetchError && fetchError?.code !== 'PGRST116') {
        console.error('Error fetching social links:', fetchError);
      } else if (data) {
        setExistingLinks(data);
        setFormData({
          instagramUrl: data?.instagram_url || '',
          instagramFollowers: data?.instagram_followers || '',
          youtubeUrl: data?.youtube_url || '',
          youtubeFollowers: data?.youtube_followers || '',
          tiktokUrl: data?.tiktok_url || '',
          tiktokFollowers: data?.tiktok_followers || '',
          twitterUrl: data?.twitter_url || '',
          twitterFollowers: data?.twitter_followers || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch social links:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateURL = (url, platform) => {
    if (!url) return null;
    
    const urlPattern = /^https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/;
    
    if (!urlPattern?.test(url)) {
      return `Invalid ${platform} URL format`;
    }
    return null;
  };

  const validateForm = () => {
    const errors = {};

    platforms?.forEach(platform => {
      const url = formData?.[platform?.urlField];
      const followers = formData?.[platform?.followersField];

      if (url) {
        const urlError = validateURL(url, platform?.name);
        if (urlError) {
          errors[platform?.urlField] = urlError;
        }

        if (followers && (isNaN(followers) || parseInt(followers) < 0)) {
          errors[platform?.followersField] = 'Must be a valid number';
        }
      }
    });

    setFormErrors(errors);
    return Object.keys(errors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (formErrors?.[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const socialLinksData = {
        user_id: user?.id,
        instagram_url: formData?.instagramUrl || null,
        instagram_followers: formData?.instagramFollowers ? parseInt(formData?.instagramFollowers) : null,
        youtube_url: formData?.youtubeUrl || null,
        youtube_followers: formData?.youtubeFollowers ? parseInt(formData?.youtubeFollowers) : null,
        tiktok_url: formData?.tiktokUrl || null,
        tiktok_followers: formData?.tiktokFollowers ? parseInt(formData?.tiktokFollowers) : null,
        twitter_url: formData?.twitterUrl || null,
        twitter_followers: formData?.twitterFollowers ? parseInt(formData?.twitterFollowers) : null
      };

      let result;
      if (existingLinks) {
        result = await supabase
          ?.from('social_links')
          ?.update(socialLinksData)
          ?.eq('id', existingLinks?.id)
          ?.select()
          ?.single();
      } else {
        result = await supabase
          ?.from('social_links')
          ?.insert([socialLinksData])
          ?.select()
          ?.single();
      }

      if (result?.error) {
        throw result?.error;
      }

      navigate('/profile-setup-checklist');
    } catch (err) {
      console.error('Failed to save social links:', err);
      setError(err?.message || 'Failed to save social links');
    } finally {
      setSaving(false);
    }
  };

  const getPlatformBadge = (platform) => {
    if (!existingLinks) return null;

    const isVerified = existingLinks?.[platform?.verifiedField];
    const hasUrl = formData?.[platform?.urlField];

    if (!hasUrl) return null;

    return (
      <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
        isVerified 
          ? 'bg-success/10 text-success' :'bg-warning/10 text-warning'
      }`}>
        <Icon 
          name={isVerified ? 'CheckCircle2' : 'Clock'} 
          size={14} 
          color={isVerified ? 'var(--color-success)' : 'var(--color-warning)'}
        />
        <span>{isVerified ? 'Verified' : 'Pending'}</span>
      </div>
    );
  };

  const formatFollowerCount = (count) => {
    if (!count) return '0';
    const num = parseInt(count);
    if (num >= 1000000) {
      return `${(num / 1000000)?.toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000)?.toFixed(1)}K`;
    }
    return num?.toString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading social links...</p>
          </div>
        </div>
        <MobileNav />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24">
        {/* Header Section */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/profile-setup-checklist')}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <Icon name="ArrowLeft" size={20} />
            <span>Back to Checklist</span>
          </button>
          
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
              <Icon name="Share2" size={24} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-foreground">Social Media Links</h1>
              <p className="text-muted-foreground">Connect your social accounts and verify follower counts</p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-lg flex items-start gap-3">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" />
            <div className="flex-1">
              <p className="text-error font-medium">Error</p>
              <p className="text-error/80 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {platforms?.map((platform) => (
            <div key={platform?.id} className="bg-card border border-border rounded-xl p-6 shadow-sm">
              {/* Platform Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${platform?.color} flex items-center justify-center`}>
                    <Icon name={platform?.icon} size={20} color="white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{platform?.name}</h3>
                    {formData?.[platform?.followersField] && (
                      <p className="text-sm text-muted-foreground">
                        {formatFollowerCount(formData?.[platform?.followersField])} followers
                      </p>
                    )}
                  </div>
                </div>
                {getPlatformBadge(platform)}
              </div>

              {/* URL Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Profile URL
                </label>
                <Input
                  type="text"
                  placeholder={platform?.urlPlaceholder}
                  value={formData?.[platform?.urlField]}
                  onChange={(e) => handleInputChange(platform?.urlField, e?.target?.value)}
                  className={formErrors?.[platform?.urlField] ? 'border-error' : ''}
                />
                {formErrors?.[platform?.urlField] && (
                  <p className="text-error text-sm mt-1">{formErrors?.[platform?.urlField]}</p>
                )}
              </div>

              {/* Follower Count Input */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Follower Count
                </label>
                <Input
                  type="number"
                  placeholder="Enter follower count"
                  value={formData?.[platform?.followersField]}
                  onChange={(e) => handleInputChange(platform?.followersField, e?.target?.value)}
                  className={formErrors?.[platform?.followersField] ? 'border-error' : ''}
                  min="0"
                />
                {formErrors?.[platform?.followersField] && (
                  <p className="text-error text-sm mt-1">{formErrors?.[platform?.followersField]}</p>
                )}
              </div>
            </div>
          ))}

          {/* Info Box */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex gap-3">
            <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
            <div className="text-sm text-foreground">
              <p className="font-medium mb-1">Verification Process</p>
              <p className="text-muted-foreground">
                After submitting your social links, our team will verify your follower counts. 
                This typically takes 24-48 hours. You'll receive a notification once verification is complete.
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/profile-setup-checklist')}
              className="flex-1"
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Saving...
                </>
              ) : (
                existingLinks ? 'Update Links' : 'Save Links'
              )}
            </Button>
          </div>
        </form>
      </main>

      <MobileNav />
    </div>
  );
};

export default SocialLinksSetup;